import React, { useState, useRef, ChangeEvent, useEffect } from 'react';

import { Button } from 'components/Button';
import { Visibility } from 'components/Visibility';
import { IOption } from 'components/Select/interface';

import arrowDown from 'img/arrow-down-grey.svg';
import IconSearch from 'img/search-icon.svg';

import { MultiSelectCustomizedProps } from './interface';
import { useOutsideClick } from 'hooks/useOutsideClick';

import {
  ArrowDown,
  MultiSelectCheckbox,
  MultiSelectFilterRow,
  MultiSelectList,
  MultiSelectOverflow,
  MultiSelectRowActionFilter,
  MultiSelectWrapper
} from './styled';

export const MultiSelectCustomized: React.FC<MultiSelectCustomizedProps> = ({
  allItems,
  itemsSelected,
  width,
  placeHolderOne,
  placeHolderMany,
  className,
  style,
  show,
  setShow
}) => {
  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, setShow);

  const [itemList, setItemList] = useState<IOption[]>([]);
  const [filteredList, setFilteredList] = useState<IOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filterThreshold = Number(process.env.REACT_APP_MULTISELECT_FILTER_THRESHOLD);
  const shouldDisplayFilter = allItems.length > filterThreshold;

  useEffect(() => {
    const options = Object.keys(allItems).map(key => ({
      value: allItems[key].key || allItems[key].name,
      label: allItems[key].value || allItems[key].name,
    }));

    setItemList(options);
    setFilteredList(options);
  }, [allItems]);

  const handleSearchClick = () => {
    const searchTermLower = searchTerm.toLowerCase().trim();
    const filtered = itemList.filter(item =>
      item.label.toLowerCase().includes(searchTermLower)
    );
    setFilteredList(filtered);
  };

  const handleToggleItem = (value: string) => {
    const updatedSelected = itemsSelected.includes(value)
      ? itemsSelected.filter(item => item !== value)
      : [...itemsSelected, value];
    itemsSelected.splice(0, itemsSelected.length, ...updatedSelected);
    setFilteredList([...filteredList]);
  };

  const handleSelectAll = () => {
    const allValues = itemList.map(item => item.value);
    itemsSelected.splice(0, itemsSelected.length, ...allValues);
    setFilteredList([...itemList]);
  };

  const handleClearAll = () => {
    itemsSelected.splice(0, itemsSelected.length);
    setSearchTerm("");
    setFilteredList([...itemList]);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);

  const setLabel = () => {
    if (itemsSelected.length === 0) return "Selecione";
    if (itemsSelected.includes('Todos')) return "Todos";
    return `${itemsSelected.length} ${itemsSelected.length > 0 ? placeHolderMany : placeHolderOne}`;
  };

  return (
    <MultiSelectWrapper ref={wrapperRef} className={className} style={style}>
      <Button className="color-black" onClick={() => setShow(!show)}>
        {setLabel()}
      </Button>

      <ArrowDown className={show ? 'arrow-down arrow-down-inverse' : 'arrow-down'} onClick={() => setShow(!show)}>
        <img src={arrowDown} alt="Toggle Dropdown" />
      </ArrowDown>

      {show && (
        <MultiSelectList width={width} show={show}>

          <Visibility visible={shouldDisplayFilter}>
            <MultiSelectFilterRow>
              <input
                type="text"
                className="form-control flex-1"
                placeholder="Buscar"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <button
                id="btn_table_search"
                tabIndex={0}
                className="focus-button"
                type="button"
                onClick={handleSearchClick}
              >
                <img src={IconSearch} alt="Search" />
              </button>
            </MultiSelectFilterRow>

            <MultiSelectRowActionFilter>
              <button onClick={handleSelectAll}>
                SELECIONAR TODOS
              </button>
              <button onClick={handleClearAll}>
                LIMPAR SELEÇÃO
              </button>
            </MultiSelectRowActionFilter>
          </Visibility>

          <MultiSelectOverflow className='overflow-y-auto'>
            {filteredList.map((opt, key) => (
              <li className="multiSelect-item d-flex align-center mb-2 mt-3" key={key}>
                <MultiSelectCheckbox
                  checked={itemsSelected.includes(opt.value)}
                  onChange={() => handleToggleItem(opt.value)}
                  className="mr-2 multiselect-checked"
                  type="checkbox"
                  value={opt.value}
                  name={`cat-${opt.value}`}
                  id={`cat-${opt.value}`}
                />
                <label htmlFor={`cat-${opt.value}`}>{opt.label}</label>
              </li>
            ))}
          </MultiSelectOverflow>
        </MultiSelectList>
      )}
    </MultiSelectWrapper>
  );
};
